import React, { use, useEffect, useState } from 'react';
import { Row } from 'antd';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  Text,
  useDisclosure,
  Select,
} from '@chakra-ui/react';
import DataTable from '../common/DataTable';
import httpInjectorService from 'services/http-injector.service';
import { toast } from 'react-toastify';
import SkeletonWithLoaders from 'components/common/Spinner';

const UserManagement = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [orgUsers, setOrgUsers] = useState([]);
  const [usersSummary, setUsersSummary] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [togglingUserId, setTogglingUserId] = useState(null);

  const validateEmails = (value) => {
    if (!value.trim()) return 'Please enter at least one email.';

    const emailList = value.split(',').map((e) => e.trim());
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    for (const e of emailList) {
      if (!regex.test(e)) {
        return `Invalid email: ${e}`;
      }
    }

    return '';
  };

  const handleSendInvite = async () => {
    const validationError = validateEmails(email);

    if (validationError) {
      setError(validationError);
      return;
    }
    const emailList = email
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    try {
      const reqBody = {
        role_id: selectedRole,
        emails: emailList,
      };

      const response = await httpInjectorService.inviteUsersToOrg(reqBody);
      if (response.status === 'success') {
        toast.success(response.message, {
          position: 'top-right',
          autoClose: 1000,
        });
        setEmail('');
        setError('');
        onClose();
        setSelectedRole('');
        OrgUsersList();
      } else {
        toast.error(response.message, {
          position: 'top-right',
          autoClose: 1000,
        });
        setEmail('');
        setError('');
        onClose();
        setSelectedRole('');
        OrgUsersList();
      }
    } catch (err) {
      toast.error(err.message, {
        position: 'top-right',
        autoClose: 1000,
      });
      setEmail('');
      setError('');
      onClose();
      setSelectedRole('');
      OrgUsersList();
    }
  };

  const OrgUsersList = async () => {
    setLoading(true);
    try {
      const response = await httpInjectorService.getOrgUsersList();
      if (response.status === 'success') {
        const usersdata = response?.data?.reduce((acc, item) => {
          return { ...acc, ...(item.users || {}) };
        });
        const usersSummary = response?.data[0]?.summary;
        setOrgUsers(usersdata.users);
        setUsersSummary(usersSummary);
      } else {
        setOrgUsers([]);
      }
    } catch (err) {
      setOrgUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    OrgUsersList();
  }, []);

  const handleChange = (event) => {
    setSelectedRole(event.target.value);
  };

  // NOTE: confirm the actual method name / payload shape your
  // httpInjectorService uses for toggling a user's active status.
  // I've assumed `updateUserStatus({ user_id, is_active })` here —
  // rename to match your real API.
  const handleToggleStatus = async (user, checked) => {
    const rowKey = user.id ?? user.user_id ?? user.username;
    const previousStatus = user.is_active;
    // Switch on -> '1', Switch off -> '0'
    const nextStatus = checked ? '1' : '0';
    setTogglingUserId(rowKey);

    // Optimistic UI update so the switch feels instant
    setOrgUsers((prev) =>
      prev.map((u) =>
        (u.id ?? u.user_id ?? u.username) === rowKey
          ? { ...u, is_active: nextStatus }
          : u
      )
    );

    try {
      const response = await httpInjectorService.updateUserStatus({
        user_id: rowKey,
        is_active: nextStatus,
      });

      if (response.status === 'success') {
        toast.success(checked ? 'User activated' : 'User deactivated', {
          position: 'top-right',
          autoClose: 1000,
        });
      } else {
        // Revert on failure
        setOrgUsers((prev) =>
          prev.map((u) =>
            (u.id ?? u.user_id ?? u.username) === rowKey
              ? { ...u, is_active: previousStatus }
              : u
          )
        );
        toast.error(response.message || 'Failed to update user status', {
          position: 'top-right',
          autoClose: 1000,
        });
      }
    } catch (err) {
      // Revert on error
      setOrgUsers((prev) =>
        prev.map((u) =>
          (u.id ?? u.user_id ?? u.username) === rowKey
            ? { ...u, is_active: previousStatus }
            : u
        )
      );
      toast.error(err.message, {
        position: 'top-right',
        autoClose: 1000,
      });
    } finally {
      setTogglingUserId(null);
    }
  };

  const columns = [
    { label: 'User', accessor: 'username' },
    { label: 'Status', accessor: 'invite_status' },
    { label: 'Last Seen', accessor: 'last_seen' },
    { label: 'Active', accessor: 'is_active' },
  ];

  return (
    <React.Fragment>
      <div className="mt-5">
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '60vh' }}
          >
            <SkeletonWithLoaders />
          </div>
        ) : (
          <div className="shadow-md p-3 rounded bg-white">
            <Row justify="space-between" align="middle">
              <h4>Users</h4>
              <Button
                float="right"
                rounded="1"
                colorScheme="purple"
                size="sm"
                onClick={onOpen}
              >
                Invite Users
              </Button>
            </Row>

            <div className="row mt-3">
              <div className="col-md-2">
                <div className="card">
                  <div className="card-body">
                    <div className="fw-bold">Total Users</div>
                    <div>{usersSummary.total_users}</div>
                  </div>
                </div>
              </div>

              <div className="col-md-2">
                <div className="card">
                  <div className="card-body">
                    <div className="fw-bold">Active Users</div>
                    <div>{usersSummary.active_users}</div>
                  </div>
                </div>
              </div>

              <div className="col-md-2">
                <div className="card">
                  <div className="card-body">
                    <div className="fw-bold">Organization Admins</div>
                    <div>{usersSummary.active_admins}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-3">
              <DataTable
                columns={columns}
                data={orgUsers}
                onToggleActive={handleToggleStatus}
                togglingRowKey={togglingUserId}
              />
            </div>
          </div>
        )}
      </div>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Invite people to your organization</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <label className="fw-bold mb-1 d-block">
              Email addresses<span className="text-danger">*</span>
            </label>

            <Input
              placeholder="Invite by email address…"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(validateEmails(e.target.value));
              }}
            />

            {error && (
              <Text color="red.500" fontSize="sm" mt={2}>
                {error}
              </Text>
            )}

            <small className="text-muted mt-2" style={{ fontSize: '0.9rem' }}>
              Separate multiple emails using a comma.
            </small>

            <div className="col-md-12 mt-3">
              <label className="fw-bold mb-1 d-block">
                Select Role<span className="text-danger">*</span>
              </label>
              <Select
                value={selectedRole}
                onChange={handleChange}
                placeholder="Select Role"
              >
                <option value="2">Admin</option>
                <option value="3">Member</option>
              </Select>
            </div>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="red" rounded="1" size="sm" onClick={onClose}>
              Cancel
            </Button>

            <Button
              colorScheme="purple"
              rounded="1"
              mx="2"
              size="sm"
              onClick={handleSendInvite}
              isDisabled={Boolean(error) || !email || !selectedRole}
            >
              Send Invite
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </React.Fragment>
  );
};

export default UserManagement;
